/**
 * EntityCard Component
 * Expandable card for an ad group or asset group showing summary and content tabs
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { AdGroup } from '@/types/campaign';
import { RSAEditor } from '@/components/features/AssetManagement/components/RSAEditor';
import { RDAEditor } from '@/components/features/AssetManagement/components/RDAEditor';
import { DemandGenAdEditor } from '@/components/features/AssetManagement/components/DemandGenAdEditor';
import type { EntityCardProps } from './types';
import { EntityTabBar } from './EntityTabBar';
import {
  getEntityName,
  getEntityId,
  getEntitySummary,
  formatEntitySummary,
  getAvailableTabs,
  getTabCounts,
} from './utils';
import {
  KeywordsPanel,
  AssetsPanel,
  RSAPanel,
  RDAPanel,
  NegativeKeywordsPanel,
  ChannelsPanel,
  DemandGenAdsPanel,
  AudiencesPanel,
} from './panels';

export function EntityCard({
  entity,
  entityType,
  isExpanded,
  onToggle,
  activeTab,
  onTabChange,
  campaign,
  selectedLandingPages,
  rdaCache,
  isLoadingRDAs,
  demandGenCache,
  isLoadingDemandGenAds,
  onEditRSA,
  onEditRDA,
  onEditDemandGenAd,
  onCreateDemandGenAd,
  editingRSA,
  editingRDA,
  editingDemandGenAd,
  memberUuid,
  getRDAInitialData,
  getDemandGenAdInitialData,
  onEditorSaved,
  onEditorCancel,
}: EntityCardProps) {
  const name = getEntityName(entity, entityType);
  const entityId = getEntityId(entity, entityType);
  const summary = getEntitySummary(entity, entityType, campaign);
  const summaryText = formatEntitySummary(summary, entityType);
  const campaignType = campaign.campaign_type || '';
  const availableTabs = getAvailableTabs(campaignType, entityType);
  const tabCounts = getTabCounts(entity, entityType, campaign);

  // Ensure active tab is valid for this entity
  const validActiveTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0];

  // Check if we're editing an RSA/RDA/DemandGenAd that belongs to this entity
  const isEditingRSAForThisEntity = editingRSA?.adGroupId === entityId;
  const isEditingRDAForThisEntity = editingRDA?.adGroupId === entityId;
  const isEditingDemandGenAdForThisEntity = editingDemandGenAd?.adGroupId === entityId;

  // Get ad group name for editor display
  const adGroupName = useMemo(() => {
    if (entityType !== 'ad_group') return '';
    const adGroup = entity as AdGroup;
    return adGroup.ad_group_name || adGroup.ads_group_name || '';
  }, [entity, entityType]);

  // Ref for scroll position preservation
  const headerRef = useRef<HTMLDivElement>(null);

  // Toggle with scroll position preservation
  const handleToggleWithScrollPreserve = useCallback(() => {
    if (!headerRef.current) {
      onToggle();
      return;
    }

    // Capture position before toggle
    const rect = headerRef.current.getBoundingClientRect();
    const topBefore = rect.top;

    // Perform toggle
    onToggle();

    // After DOM update, adjust scroll to keep header in same position
    requestAnimationFrame(() => {
      if (headerRef.current) {
        const topAfter = headerRef.current.getBoundingClientRect().top;
        const diff = topAfter - topBefore;
        if (diff !== 0) {
          window.scrollBy(0, diff);
        }
      }
    });
  }, [onToggle]);

  // Handle keyboard navigation for the header
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggleWithScrollPreserve();
      }
    },
    [handleToggleWithScrollPreserve]
  );

  // Handle RSA edit - pass through to parent
  const handleEditRSA = useCallback(
    (adId: string) => {
      if (onEditRSA) {
        onEditRSA(entityId, adId);
      }
    },
    [onEditRSA, entityId]
  );

  // Handle RDA edit - pass through to parent
  const handleEditRDA = useCallback(
    (adId: string) => {
      if (onEditRDA) {
        onEditRDA(entityId, adId);
      }
    },
    [onEditRDA, entityId]
  );

  // Handle Demand Gen ad edit - pass through to parent
  const handleEditDemandGenAd = useCallback(
    (adId: string) => {
      if (onEditDemandGenAd) {
        onEditDemandGenAd(entityId, adId);
      }
    },
    [onEditDemandGenAd, entityId]
  );

  // Render panel content based on active tab
  const renderPanelContent = () => {
    // For ad groups, we can render keyword and RSA/RDA panels
    const adGroup = entityType === 'ad_group' ? (entity as AdGroup) : null;

    switch (validActiveTab) {
      case 'keywords':
        if (!adGroup) {
          return (
            <div className="p-4 text-sm text-gray-500">
              Keywords are only available for ad groups.
            </div>
          );
        }
        return (
          <KeywordsPanel
            adGroup={adGroup}
            campaign={campaign}
            selectedLandingPages={selectedLandingPages}
          />
        );

      case 'assets':
        return (
          <AssetsPanel
            entity={entity}
            entityType={entityType}
            campaign={campaign}
            selectedLandingPages={selectedLandingPages}
            rdaCache={rdaCache}
            isLoadingRDAs={isLoadingRDAs}
          />
        );

      case 'rsas':
        if (!adGroup) {
          return (
            <div className="p-4 text-sm text-gray-500">
              RSAs are only available for ad groups.
            </div>
          );
        }
        // Show editor inline if editing an RSA for this entity
        if (isEditingRSAForThisEntity && editingRSA && memberUuid && onEditorSaved && onEditorCancel) {
          return (
            <div className="p-4">
              <RSAEditor
                memberUuid={memberUuid}
                adGroupId={editingRSA.adGroupId}
                adId={editingRSA.adId}
                customerId={campaign.google_ads_account_id}
                adGroupName={adGroupName}
                onSaved={onEditorSaved}
                onCancel={onEditorCancel}
              />
            </div>
          );
        }
        return (
          <RSAPanel
            adGroup={adGroup}
            onEditRSA={handleEditRSA}
          />
        );

      case 'rdas':
        if (!adGroup) {
          return (
            <div className="p-4 text-sm text-gray-500">
              RDAs are only available for ad groups.
            </div>
          );
        }
        // Show editor inline if editing an RDA for this entity
        if (isEditingRDAForThisEntity && editingRDA && memberUuid && onEditorSaved && onEditorCancel) {
          return (
            <div className="p-4">
              <RDAEditor
                memberUuid={memberUuid}
                campaignId={campaign.campaign_id}
                adGroupId={editingRDA.adGroupId}
                adId={editingRDA.adId}
                customerId={campaign.google_ads_account_id}
                adGroupName={adGroupName}
                initialData={getRDAInitialData?.(editingRDA.adId)}
                onSaved={onEditorSaved}
                onCancel={onEditorCancel}
              />
            </div>
          );
        }
        return (
          <RDAPanel
            adGroup={adGroup}
            rdaCache={rdaCache}
            isLoadingRDAs={isLoadingRDAs}
            onEditRDA={handleEditRDA}
          />
        );

      case 'negative-keywords':
        return <NegativeKeywordsPanel campaign={campaign} />;

      case 'channels':
        if (!adGroup) {
          return (
            <div className="p-4 text-sm text-gray-500">
              Channel controls are only available for ad groups.
            </div>
          );
        }
        return (
          <ChannelsPanel
            adGroup={adGroup}
            campaign={campaign}
          />
        );

      case 'demand-gen-ads':
        if (!adGroup) {
          return (
            <div className="p-4 text-sm text-gray-500">
              Demand Gen ads are only available for ad groups.
            </div>
          );
        }
        // Show editor inline if editing a Demand Gen ad for this entity
        if (isEditingDemandGenAdForThisEntity && editingDemandGenAd && memberUuid && onEditorSaved && onEditorCancel) {
          return (
            <div className="p-4">
              <DemandGenAdEditor
                memberUuid={memberUuid}
                campaignId={campaign.campaign_id}
                adGroupId={editingDemandGenAd.adGroupId}
                adId={editingDemandGenAd.adId}
                customerId={campaign.google_ads_account_id}
                adGroupName={adGroupName}
                initialData={getDemandGenAdInitialData?.(editingDemandGenAd.adId)}
                onSaved={onEditorSaved}
                onCancel={onEditorCancel}
              />
            </div>
          );
        }
        return (
          <DemandGenAdsPanel
            adGroup={adGroup}
            campaign={campaign}
            demandGenCache={demandGenCache}
            isLoadingDemandGenAds={isLoadingDemandGenAds}
            onCreateAd={onCreateDemandGenAd}
            onEditAd={handleEditDemandGenAd}
          />
        );

      case 'audiences':
        if (!adGroup) {
          return (
            <div className="p-4 text-sm text-gray-500">
              Audience targeting is only available for ad groups.
            </div>
          );
        }
        return (
          <AudiencesPanel
            adGroup={adGroup}
            campaign={campaign}
          />
        );

      default:
        return (
          <div className="p-4 text-sm text-gray-500">
            Select a tab to view content
          </div>
        );
    }
  };

  return (
    <div
      className={`
        border rounded-lg transition-shadow
        focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2
        ${isExpanded ? 'shadow-md border-gray-300' : 'shadow-sm border-gray-200 hover:shadow'}
      `}
    >
      {/* Card Header - Always visible */}
      <div
        ref={headerRef}
        role="button"
        tabIndex={0}
        onClick={handleToggleWithScrollPreserve}
        onKeyDown={handleKeyDown}
        className={`
          flex items-center justify-between px-4 py-3 cursor-pointer rounded-t-lg
          transition-colors focus:outline-none
          ${isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50 rounded-b-lg'}
        `}
        aria-expanded={isExpanded}
        aria-controls={`entity-content-${entityId}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
            )}
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {name}
            </h3>
          </div>
          <p className="mt-0.5 ml-7 text-xs text-gray-500">
            {summaryText}
          </p>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div id={`entity-content-${entityId}`}>
          {/* Tab Bar */}
          {availableTabs.length > 1 && (
            <EntityTabBar
              availableTabs={availableTabs}
              activeTab={validActiveTab}
              onChange={onTabChange}
              counts={tabCounts}
            />
          )}

          {/* Panel Content */}
          <div
            role="tabpanel"
            id={`panel-${validActiveTab}`}
            aria-labelledby={`tab-${validActiveTab}`}
            className="bg-white rounded-b-lg"
          >
            {renderPanelContent()}
          </div>
        </div>
      )}
    </div>
  );
}

export default EntityCard;
